// Shared Turkish letter/keyboard policy for future DANDU dictation and
// word-completion mini-tasks. Never derive displayed keys from the answer.
export const TURKISH_ALPHABET = 'ABCÇDEFGĞHIİJKLMNOÖPRSŞTUÜVYZ';
const KNOWN = new Set([...TURKISH_ALPHABET]);

export function normalizeTurkishLetters(alphabet) {
  const source = alphabet === 'all' || alphabet === 'full' ? TURKISH_ALPHABET
    : Array.isArray(alphabet) ? alphabet.join('') : typeof alphabet === 'string' ? alphabet : '';
  const letters = [...new Set([...source.normalize('NFC')]
    .map(letter => letter.toLocaleUpperCase('tr-TR')).filter(letter => KNOWN.has(letter)))];
  return letters.length === 29 ? [...TURKISH_ALPHABET] : letters;
}

// Respect the host component as well as the viewport. Embedded keyboards and
// browser zoom must reflow rather than shrink a touch target below 44 CSS px.
export function letterKeypadLayout(letters, { columns, keyWidth, gap, availableWidth }) {
  const idealRows = balancedLetterRows(letters, columns);
  const idealCount = Math.max(1, ...idealRows.map(row => row.length));
  const maxWidth = idealCount * keyWidth + (idealCount - 1) * gap;
  const width = availableWidth > 0 ? availableWidth : maxWidth;
  const capacity = Math.max(1, Math.min(columns, Math.floor((width + gap) / (44 + gap))));
  const rows = balancedLetterRows(letters, capacity);
  const count = Math.max(1, ...rows.map(row => row.length));
  return { rows, maxWidth, keyWidth: Math.max(44, Math.min(keyWidth, (width - (count - 1) * gap) / count)) };
}

// Keep a constant order within a curriculum level, and balance the final row
// so a 29-letter phone keyboard does not end in a lonely one-key row.
export function balancedLetterRows(keys, maxColumns) {
  if (!Array.isArray(keys) || !keys.length) return [];
  const columns = Math.max(1, Math.floor(Number(maxColumns) || 1));
  const rowCount = Math.ceil(keys.length / columns);
  const base = Math.floor(keys.length / rowCount);
  const remainder = keys.length % rowCount;
  const rows = [];
  let cursor = 0;
  for (let row = 0; row < rowCount; row += 1) {
    const size = base + Number(row < remainder);
    rows.push(keys.slice(cursor, cursor + size));
    cursor += size;
  }
  return rows;
}

// The existing PaddleOCR G12 comparison deliberately excludes Turkish
// dotted/dotless I and non-ASCII Turkish characters. Match that contract
// exactly; a plain ASCII I is unsupported as well.
export function supportsG12Freehand(items) {
  return Array.isArray(items) && items.length > 0 && items.every(item => {
    const word = item && typeof item.expected_word === 'string' ? item.expected_word.trim() : '';
    return !!word && /^[A-HJ-Z]+$/.test(word.normalize('NFC').toLocaleUpperCase('tr-TR'));
  });
}
